/* eslint-disable react/prop-types */
import React, { ChangeEvent, useContext, useEffect, useState } from 'react';

import Cell from './V5Gadget/Matrix/Cell';
import BoardContext from '../contexts/BoardContext';

import styled from 'styled-components';
import assignReasonCode from '../api/reasonCodes/assignReasonCode';
import Loading from './Loading';
import type { ICellEditorParams, ICellRendererParams } from 'ag-grid-community';
import type { CustomCellEditorProps } from 'ag-grid-react';
import Row from './Common/Row';
import Button from 'kingpin/atoms/Button';
import GridCellErrorBoundary from './GridCellErrorBoundary';
import GqlClientContext from '../contexts/GqlClientContext';
import AnalyticsContext from '../contexts/AnalyticsContext';
import Typography from 'kingpin/atoms/Typography';
import removeReasonCode from '../api/reasonCodes/removeReasonCode';
import Tooltip from './Tooltip';
import useValidateText from 'hooks/useValidateText';
import Icon from '../kingpin/atoms/Icon';
import KingpinBadge from './Badge';
import TextInput from '../kingpin/atoms/TextInput';
import TextArea from './Inputs/TextArea';

const List = styled.div`
  max-height: 160px;
  overflow-y: scroll;
  margin-top: 8px;
  padding: 0px 12px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  height: 32px;
  &:hover {
    background-color: var(--color-surface-secondary);
  }
`;

const Popup = styled.div`
  padding: 24px 16px;
  background-color: var(--color-surface-primary);
`;

export const Badge = styled.div`
  border-radius: 2px;
  background-color: var(--color-surface-secondary);
  padding: 6px 8px;
`;

export const BadgeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 2em;
`;

const Warning = styled.div`
  color: var(--color-status-danger);
  font-size: 13px;
`;

const INVALID_REASON_NOTE_TOOLTIP =
  'Your summary contains illegal characters (":", ";", "<", ">").';

export const ReasonCodePickerRenderer = (props: CustomCellEditorProps) => {
  const { board } = useContext(BoardContext);

  // Reactive cell-editor value flow: onValueChange updates the grid's editor
  // value synchronously, stopEditing then commits it. Popup behaviour is
  // declared on the column def via `cellEditorPopup`.
  const setValue = ({
    reasonCode,
    reasonNote,
  }: {
    reasonCode: string[] | undefined;
    reasonNote: string | undefined;
  }) => {
    if (props.api.getColumn('reasonNote')) {
      props.node.setDataValue('reasonNote', reasonNote);
    }
    props.onValueChange(reasonCode);
    props.stopEditing();
  };

  if (!board) {
    return null;
  }

  return (
    <ReasonPicker
      documentId={props.data.id}
      agGridProps={props}
      setValue={setValue}
    />
  );
};

const ReasonPicker = ({
  documentId,
  agGridProps,
  setValue,
}: {
  documentId: string;
  agGridProps: ICellEditorParams;
  setValue: ({
    reasonCode,
    reasonNote,
  }: {
    reasonCode: string[] | undefined;
    reasonNote: string | undefined;
  }) => void;
}) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const { client } = useContext(GqlClientContext);
  const { reasonCodes, board } = useContext(BoardContext);
  const selectedCodes = agGridProps.value
    ? (agGridProps.value as string[])
    : [];
  const selectedReasonCodes = (() => {
    if (Array.isArray(selectedCodes)) {
      return selectedCodes
        .map((c) => reasonCodes.find((rc) => rc.code === c))
        .filter((rc) => !!rc) as ReasonCode[];
    } else {
      return [];
    }
  })();

  const [searchText, setSearchText] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [wipCodes, setWipCodes] = useState<ReasonCode[]>(selectedReasonCodes);
  const [reasonSummary, setReasonSummary] = useState<string | undefined>(
    agGridProps.data.reasonNote,
  );
  const [isReasonSummaryValid, setIsReasonSummaryValid] =
    useState<boolean>(true);
  const [isValid, setIsValid] = useState<boolean>(false);
  const hasNoCodes = wipCodes.length === 0;
  const validateText = useValidateText();

  // setIsReasonSummaryValid
  useEffect(() => {
    if (reasonSummary === undefined || reasonSummary === null) {
      setIsReasonSummaryValid(true);
      return;
    }

    setIsReasonSummaryValid(validateText({ text: reasonSummary }));
  }, [reasonSummary, validateText]);

  const onSearchTextChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const filteredReasonCodes = reasonCodes
    .filter((r) => r.code.toLowerCase().includes(searchText.toLowerCase()))
    .sort((a, b) => a.code.localeCompare(b.code));
  const hasFilteredCodes = filteredReasonCodes.length > 0;

  // setIsValid
  useEffect(() => {
    const isCodeValid =
      selectedCodes.length === 0 ? wipCodes.length !== 0 : true;
    const newIsValid = isCodeValid && isReasonSummaryValid;
    setIsValid(newIsValid);
  }, [isReasonSummaryValid, selectedCodes.length, wipCodes.length]);

  const toggleWipCode = (code: ReasonCode) => {
    const isExisting = !!wipCodes.find((c) => c.id === code.id);
    if (isExisting) {
      setWipCodes((codes) => codes.filter((c) => c.id !== code.id));
    } else {
      setWipCodes((codes) => [...codes, code]);
    }
  };

  const onReasonSummaryChanged = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setReasonSummary(event.target.value);
  };

  const onSave = () => {
    if (!isValid) {
      return;
    }

    setIsSaving(true);

    if (wipCodes.length > 0) {
      assignReasonCode({
        documentId,
        documentType: board.dataType,
        reasonCodes: wipCodes,
        reasonNote: reasonSummary,
        client,
      }).then(() => {
        const newCodes = wipCodes.map((c) => c.code);
        setValue({
          reasonCode: newCodes,
          reasonNote: reasonSummary,
        });

        trackEvent('Reason code - Add code clicked');
        if (reasonSummary) {
          trackEvent('Reason code - Summary - added');
        }
        setIsSaving(false);
      });
    } else {
      removeReasonCode({
        documentId,
        documentType: board.dataType,
        client,
      }).then(() => {
        setValue({
          reasonCode: undefined,
          reasonNote: undefined,
        });

        setIsSaving(false);
      });
    }
  };

  return (
    <Popup>
      {isSaving && <Loading />}
      {!isSaving && (
        <React.Fragment>
          <div style={{ fontSize: 11 }}>
            <div style={{ marginBottom: 16 }}>
              <TextInput
                value={searchText}
                onChange={onSearchTextChanged}
                icon="search"
                autoFocus
                inputSize="Small"
              />
              <List>
                {!hasFilteredCodes && <div>No Reason Codes were found</div>}
                {hasFilteredCodes &&
                  filteredReasonCodes.map((r) => (
                    <Item
                      key={r.id}
                      onClick={() => {
                        toggleWipCode(r);
                      }}
                    >
                      <Row
                        centerAlign
                        style={{
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Typography.Body type="Body 12">
                          {r.code}
                        </Typography.Body>
                        {!!wipCodes.find((c) => c.id === r.id) && (
                          <Icon icon="checkmark" />
                        )}
                      </Row>
                    </Item>
                  ))}
              </List>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body type="Label">Summary</Typography.Body>
              </div>
              <TextArea
                isReadOnly={hasNoCodes}
                readOnly={hasNoCodes}
                value={reasonSummary}
                onChange={onReasonSummaryChanged}
              />
              {hasNoCodes && reasonSummary && (
                <Warning>* This will be lost</Warning>
              )}
            </div>
            <Row
              centerAlign
              style={{ justifyContent: 'space-between', width: '100%' }}
            >
              <Button
                size="Small"
                type="Ghost"
                onClick={() => agGridProps.stopEditing()}
                label="Cancel"
              />
              <Tooltip
                content={
                  isReasonSummaryValid ? undefined : INVALID_REASON_NOTE_TOOLTIP
                }
              >
                <Button
                  size={'Small'}
                  type={'Primary'}
                  label="Save"
                  isDisabled={!isValid}
                  onClick={onSave}
                />
              </Tooltip>
            </Row>
          </div>
        </React.Fragment>
      )}
    </Popup>
  );
};

interface ReasonCodeCellParams extends ICellRendererParams {
  isOnClickEnabled: boolean;
}

class ReasonCodeCellRenderer extends React.Component<ReasonCodeCellParams> {
  render() {
    const { isOnClickEnabled } = this.props;
    const value = this.props.value;
    const { node } = this.props;

    if (node.rowPinned) {
      return <Cell />;
    }

    const onClick = () => {
      // Do nothing
    };

    if (isOnClickEnabled) {
      if (value && value.length !== 0) {
        return (
          <Cell onClick={onClick}>
            <KingpinBadge
              text={Array.isArray(value) ? value.join(', ') : value}
              badgeType={'Default'}
            />
          </Cell>
        );
      } else {
        return (
          <Cell
            onClick={onClick}
            style={{ color: 'var(--color-navy)', cursor: 'pointer' }}
          >
            <span style={{ color: 'var(--color-info-v2)' }}>Add code</span>
          </Cell>
        );
      }
    } else {
      if (value && value.length !== 0) {
        return (
          <Cell>
            <KingpinBadge
              text={Array.isArray(value) ? value.join(', ') : value}
              badgeType={'Default'}
            />
          </Cell>
        );
      } else {
        return <Cell>-</Cell>;
      }
    }
  }
}

const Gate = (params: ICellRendererParams) => {
  const { board } = useContext(BoardContext);

  return (
    <GridCellErrorBoundary
      params={params}
      childComponent={
        <ReasonCodeCellRenderer {...params} isOnClickEnabled={!!board} />
      }
    />
  );
};

export default Gate;
